import { Field, Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import CustomSelect from "../../../Components/Utils/CustomSelect";
import Input from "../../../Components/Utils/Input";
import BaseApi from "../../../Http/BaseApi";
import * as Yup from "yup";
import { ThreeDots } from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../Components/Utils/Button";
import { getBudgetSuperMastersAsync } from "../../../Redux/BudgetSlice";
import { getAcademicSessionsAsync } from "../../../Redux/MasterSlice";

const emptyFormValues = {
  id: null,
  amount: "",
  acc_budget_master_id: "",
  acc_payment_head_id: "",
  academic_session_id: "",
};

function paymentHeadLabelWithCode(name, code) {
  const n = name ?? "";
  if (code != null && String(code).trim() !== "") {
    return `${n} [${code}]`;
  }
  return n;
}

export default function BudgetBlock() {
  const [errorMessage, setErrorMessage] = useState(false);
  const [success, setSuccess] = useState(false);
  const formikRef = useRef();
  const [budgetBlocks, setBudgetBlocks] = useState([]);
  const [accPaymentHeads, setAccPaymentHeads] = useState([]);

  const [loading, setLoading] = useState(false);
  const [formRole, setFormRole] = useState("add");
  const [formValues, setFormValues] = useState(emptyFormValues);
  const [successKind, setSuccessKind] = useState(null);
  /** Same pattern as CostCenter nodal person: defaultInputValue + key on CustomSelect */
  const [sessionDefaultInput, setSessionDefaultInput] = useState();
  const [budgetMasterDefaultInput, setBudgetMasterDefaultInput] = useState();
  const [paymentHeadDefaultInput, setPaymentHeadDefaultInput] = useState();
  const dispatch = useDispatch();
  const master = useSelector((state) => state.master);
  const budget = useSelector((state) => state.budget);
  const budgetSuperMasters = [...budget.budgetSuperMasters];
  const academicSessions = [...master.academicSessions];
  const [deleteConfirm, setDeleteConfirm] = useState({ state: 0 });

  const validationSchema = Yup.object().shape({
    amount: Yup.string().required("This field is required!"),
    acc_budget_master_id: Yup.string().required("This field is required!"),
    acc_payment_head_id: Yup.string().required("This field is required!"),
    academic_session_id: Yup.string().required("This field is required!"),
  });

  useEffect(() => {
    if (budgetBlocks.length == 0) {
      getBudgetBlocks();
    }
    if (academicSessions.length === 0) {
      dispatch(getAcademicSessionsAsync());
    }
    if (budgetSuperMasters.length === 0) {
      dispatch(getBudgetSuperMastersAsync());
    }
  }, [dispatch]);

  const getBudgetBlocks = async () => {
    setLoading(true);
    BaseApi()
      .get(`/acc-budget-blocks`)
      .then((response) => {
        setBudgetBlocks(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.log("error is", error);
        setLoading(false);
      });
  };

  const handleDelete = async (event, payload) => {
    event.target.disabled = true;

    if (deleteConfirm.state == 1) {
      await BaseApi()
        .delete(`acc-budget-blocks/${payload.id}`, {})
        .then(() => {
          setBudgetBlocks((prev) => prev.filter((p) => p.id != payload.id));
        })
        .catch((error) => {
          const message =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
          setErrorMessage(message);
        });
    }
    event.target.disabled = false;
    setDeleteConfirm({});
  };

  const callAccHeads = async (acc_budget_master_id) => {
    setLoading(true);
    return BaseApi()
      .get(
        `/acc-payment-heads?type=2&acc_budget_master_id=${acc_budget_master_id}`
      )
      .then((response) => {
        setAccPaymentHeads(response.data);
        setLoading(false);
        return response.data;
      })
      .catch((error) => {
        console.log("error is", error);
        setLoading(false);
        throw error;
      });
  };

  const resetFormToAdd = () => {
    setFormRole("add");
    setFormValues(emptyFormValues);
    setAccPaymentHeads([]);
    setErrorMessage(false);
    setSessionDefaultInput(undefined);
    setBudgetMasterDefaultInput(undefined);
    setPaymentHeadDefaultInput(undefined);
  };

  const cancelEdit = () => {
    resetFormToAdd();
    setSuccess(false);
    setSuccessKind(null);
  };

  const onEdit = async (event, budget) => {
    event.preventDefault();
    setErrorMessage(false);
    setSuccess(false);
    setSuccessKind(null);
    setLoading(true);
    try {
      setSessionDefaultInput(budget.academic_session?.label);
      setBudgetMasterDefaultInput(budget.acc_budget_master?.name);

      const headsData = await callAccHeads(budget.acc_budget_master_id);
      const flatHeads = restructureArray(headsData);
      const headRow = flatHeads.find(
        (h) => String(h.id) === String(budget.acc_payment_head_id)
      );
      setPaymentHeadDefaultInput(
        headRow?.name ??
          paymentHeadLabelWithCode(
            budget.acc_payment_head?.name,
            budget.acc_payment_head?.code
          )
      );

      setFormValues({
        id: budget.id,
        amount: String(budget.amount ?? ""),
        acc_budget_master_id: String(budget.acc_budget_master_id ?? ""),
        acc_payment_head_id: String(budget.acc_payment_head_id ?? ""),
        academic_session_id: String(
          budget.academic_session_id ?? budget.academic_session?.id ?? ""
        ),
      });
      setFormRole("edit");
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, { resetForm }) => {
    setErrorMessage(false);
    setSuccess(false);
    setSuccessKind(null);
    const { id, ...body } = payload;

    try {
      if (formRole === "edit" && id != null && id !== "") {
        await BaseApi()
          .put(`acc-budget-blocks/${id}`, body)
          .then((res) => {
            setBudgetBlocks((prev) =>
              prev.map((b) => (b.id === id ? res.data : b))
            );
            resetFormToAdd();
            resetForm({ values: emptyFormValues });
            setSuccess(true);
            setSuccessKind("updated");
          });
      } else {
        await BaseApi()
          .post(`acc-budget-blocks`, body)
          .then((item) => {
            resetFormToAdd();
            resetForm({ values: emptyFormValues });
            setBudgetBlocks((prev) => [...prev, item.data]);
            setSuccess(true);
            setSuccessKind("saved");
          });
      }
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      setErrorMessage(message);
    }
  };

  function restructureArray(arr, level = 0) {
    let result = [];
    for (let i = 0; i < arr.length; i++) {
      let item = arr[i];
      let indent = " ".repeat(level * 4);
      const label = paymentHeadLabelWithCode(item.name, item.code);
      let newItem = { id: item.id, name: indent + label };
      result.push(newItem);
      if (item.children && item.children.length > 0) {
        let children = restructureArray(item.children, level + 1);
        result = result.concat(children);
      }
    }
    return result;
  }

  let thClass =
    " text-gray-600   border p-2  whitespace-nowrap dark:text-gray-400 font-bold pr-6 text-left text-sm tracking-normal leading-4";

  let tdClass =
    " text-gray-600 border p-2   dark:text-gray-400 font-normal pr-6 text-left text-sm m-4 tracking-normal leading-4";

  return (
    <>
      <div className='container mx-auto'>
        <h1 className='text-2xl font-bold mb-4'>Block Previous Amounts</h1>
        {loading == true && (
          <>
            <div className='flex'>
              <span className='m-auto'>
                <ThreeDots
                  height='100'
                  width='100'
                  color='grey'
                  ariaLabel='loading'
                />
              </span>
            </div>
          </>
        )}
        {success && (
          <p className='text-green-500 mb-4'>
            Data {successKind === "updated" ? "updated" : "saved"}{" "}
            successfully!
          </p>
        )}
        {errorMessage && <p className='text-red-500 mb-4'>{errorMessage}</p>}
        <Formik
          innerRef={formikRef}
          enableReinitialize={true}
          initialValues={formValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className='space-y-4'>
              {formRole === "edit" && formValues.id != null && (
                <Field type='hidden' name='id' />
              )}
              {formRole === "edit" && (
                <p className='text-sm font-medium text-blue-700'>
                  Editing block #{formValues.id} — change fields and click
                  Update, or Cancel to discard.
                </p>
              )}
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4'>
                <CustomSelect
                  key={`fy-${formValues.academic_session_id}`}
                  defaultInputValue={sessionDefaultInput}
                  options={academicSessions}
                  name='academic_session_id'
                  label='Select Financial Year'
                  isSearchable={true}
                  getOptionValue={(option) => `${option}`}
                  getOptionLabel={(option) => `${option.label} `}
                  onChange={(selectedOption) => {
                    formikRef.current?.setFieldValue(
                      "academic_session_id",
                      selectedOption ? selectedOption.id : ""
                    );
                  }}
                />
                <CustomSelect
                  key={`bm-${formValues.acc_budget_master_id}`}
                  defaultInputValue={budgetMasterDefaultInput}
                  options={budgetSuperMasters}
                  name='acc_budget_master_id'
                  label='Select Budget Part'
                  isSearchable={true}
                  getOptionValue={(option) => `${option}`}
                  getOptionLabel={(option) => `${option.name} `}
                  onChange={(selectedOption) => {
                    const masterId = selectedOption ? selectedOption.id : "";
                    formikRef.current?.setFieldValue(
                      "acc_budget_master_id",
                      masterId
                    );
                    setPaymentHeadDefaultInput(undefined);
                    if (selectedOption) {
                      callAccHeads(selectedOption.id);
                    } else {
                      setAccPaymentHeads([]);
                      formikRef.current?.setFieldValue(
                        "acc_payment_head_id",
                        ""
                      );
                    }
                  }}
                />
                <CustomSelect
                  key={`ph-${formValues.acc_payment_head_id}`}
                  defaultInputValue={paymentHeadDefaultInput}
                  options={restructureArray(accPaymentHeads)}
                  formatOptionLabel={(option) => {
                    return <pre>{option.name}</pre>;
                  }}
                  name='acc_payment_head_id'
                  label='Select Head of Account'
                  isSearchable={true}
                  getOptionValue={(option) => `${option}`}
                  getOptionLabel={(option) => `${option.name}   `}
                  onChange={(selectedOption) => {
                    formikRef.current?.setFieldValue(
                      "acc_payment_head_id",
                      selectedOption ? selectedOption.id : ""
                    );
                  }}
                />
                <div className='flex flex-col'>
                  <Input
                    label='Amount'
                    placeholder='Enter Amount'
                    type='number'
                    name='amount'
                    className='border rounded-md p-2'
                  />
                </div>
              </div>

              <div className='flex justify-center  gap-3 flex-wrap'>
                <Button
                  type={"submit"}
                  disabled={isSubmitting}
                  value={formRole === "edit" ? "Update" : "Submit"}
                  icon={formRole === "edit" ? "SaveIcon" : "SaveIcon"}
                />
                {formRole === "edit" && (
                  <Button
                    type='button'
                    onClick={() => {
                      cancelEdit();
                      formikRef.current?.resetForm({ values: emptyFormValues });
                    }}
                    value='Cancel'
                    icon={"XIcon"}
                  />
                )}
              </div>
            </Form>
          )}
        </Formik>
        <br />
        <table
          id='datatable'
          className='min-w-full   bg-white dark:bg-gray-800'
        >
          <thead>
            <tr>
              <th className={thClass + "w-16"}>#</th>
              <th className={thClass}>Financial Year</th>
              <th className={thClass}>Budget Type</th>
              <th className={thClass}>Payment Head</th>
              <th className={thClass}>Ledger Code</th>
              <th className={thClass}>Amount</th>

              <th className={thClass}>Action</th>
            </tr>
          </thead>
          <tbody>
            {budgetBlocks.length > 0 &&
              budgetBlocks.map((budget, i) => {
                return (
                  <tr key={budget.id} className='odd:bg-white even:bg-gray-50'>
                    <td className={tdClass + "w-32"}>{++i}</td>
                    <td className={tdClass}>
                      {budget.academic_session?.label ?? "—"}
                    </td>
                    <td className={tdClass}>
                      {budget.acc_budget_master?.name}
                    </td>
                    <td className={tdClass}>
                      {" "}
                      {budget.acc_payment_head?.name}
                    </td>
                    <td className={tdClass}>{budget.acc_payment_head?.code}</td>
                    <td className={tdClass}>{budget.amount}</td>

                    <td className={tdClass}>
                      {(deleteConfirm.state == 0 ||
                        deleteConfirm.itemId != budget.id) && (
                        <div className='flex justify-center w-full gap-3 flex-wrap items-center'>
                          <button
                            type='button'
                            className='mr-3 text-blue-600 hover:text-blue-800 underline font-light py-1 px-2 rounded'
                            onClick={(e) => onEdit(e, budget)}
                          >
                            Edit
                          </button>
                          <span
                            className='ml-5 bg-gradient-to-r from-red-500 to-yellow-500 hover:bg-red-700 text-white font-light py-1 px-2 rounded cursor-pointer'
                            onClick={() =>
                              setDeleteConfirm({
                                itemId: budget.id,
                                state: 1,
                              })
                            }
                          >
                            Delete
                          </span>
                        </div>
                      )}
                      {deleteConfirm.state == 1 &&
                        deleteConfirm.itemId == budget.id && (
                          <>
                            <p className='text-red-700'> Are You Sure? </p>

                            <button
                              className='outline-button-danger'
                              onClick={(event) => handleDelete(event, budget)}
                            >
                              Yes
                            </button>
                            <span className='mr-2'></span>
                            <button
                              className='outline-button-success'
                              onClick={() => setDeleteConfirm({})}
                            >
                              No
                            </button>
                          </>
                        )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </>
  );
}
